import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useToastWithSound } from '../hooks/useToastWithSound';
import { login, clearError } from '../store/authSlice';
import LockCountdownTimer from '../components/LockCountdownTimer';

// Custom CSS animations
const customStyles = `
  @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-10px); }
  }
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(30px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes slideUp {
    from { opacity: 0; transform: translateY(50px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .animate-float {
    animation: float 3s ease-in-out infinite;
  }
  .animate-fade-in {
    animation: fadeIn 1s ease-out;
  }
  .animate-slide-up {
    animation: slideUp 0.8s ease-out forwards;
    opacity: 0;
  }
`;

// Add styles to document head
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement('style');
  styleSheet.textContent = customStyles;
  document.head.appendChild(styleSheet);
}

const LoginScreen = () => {
  // Set dynamic page title
  useEffect(() => {
    document.title = 'Login - Shahji Computers ERP';
  }, []);
  const [mobileNumber, setMobileNumber] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [otp, setOtp] = useState('');
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [branch, setBranch] = useState('Katra');
  const [userRole, setUserRole] = useState(null); // 'Admin', 'Staff', or null
  const [showBranchSelection, setShowBranchSelection] = useState(false);
  const [userExists, setUserExists] = useState(false);
  const [isAccountLocked, setIsAccountLocked] = useState(false);
  const [lockTimeMinutes, setLockTimeMinutes] = useState(0);
  const [checkingUser, setCheckingUser] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [locationError, setLocationError] = useState('');
  const [gettingLocation, setGettingLocation] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showActionToast, showError } = useToastWithSound();

  const { userInfo, loading, error } = useSelector((state) => state.auth);

  // Get user location on component mount
  useEffect(() => {
    const getUserLocation = () => {
      if (!navigator.geolocation) {
        setLocationError('Geolocation is not supported by this browser.');
        return;
      }

      setGettingLocation(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
          setGettingLocation(false);
          setLocationError('');
        },
        (error) => {
          setGettingLocation(false);
          switch (error.code) {
            case error.PERMISSION_DENIED:
              setLocationError('Location access denied. Please enable location services to continue.');
              break;
            case error.POSITION_UNAVAILABLE:
              setLocationError('Location information is unavailable.');
              break;
            case error.TIMEOUT:
              setLocationError('Location request timed out.');
              break;
            default:
              setLocationError('An unknown error occurred while retrieving location.');
              break;
          }
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000
        }
      );
    };

    getUserLocation();
  }, []);

  useEffect(() => {
    if (userInfo) {
      showActionToast('login', true);
      if (userInfo.mustChangePassword) {
        navigate('/change-password');
      } else if (userInfo.role === 'Admin') {
        navigate('/admin-dashboard');
      } else {
        navigate('/staff-dashboard');
      }
    }
  }, [navigate, userInfo, showActionToast]);

  // Function to check user role based on mobile number
  const checkUserRole = async (mobile) => {
    if (!mobile || mobile.length < 10) {
      setUserRole(null);
      setUserExists(false);
      setShowBranchSelection(false);
      return;
    }

    setCheckingUser(true);
    try {
      console.log('Checking user role for:', mobile);
      const response = await fetch('/api/users/check-role', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ mobileNumber: mobile }),
      });

      console.log('API Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('User data received:', data);
        setUserRole(data.role);
        setUserExists(true);
        setShowBranchSelection(data.role === 'Staff');
      } else {
        const errorData = await response.text();
        console.log('API Error:', response.status, errorData);
        setUserRole(null);
        setUserExists(false);
        setShowBranchSelection(false);
      }
    } catch (error) {
      console.error('Network error checking user role:', error);
      setUserRole(null);
      setUserExists(false);
      setShowBranchSelection(false);
    } finally {
      setCheckingUser(false);
    }
  };

  // Handle mobile number change with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (mobileNumber) {
        checkUserRole(mobileNumber);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [mobileNumber]);

  const submitHandler = (e) => {
    e.preventDefault();
    
    // Don't allow login if account is locked
    if (isAccountLocked) {
      showError('Account is locked. Please wait for the lock period to expire.');
      return;
    }

    // Check location access
    if (!userLocation) {
      if (locationError) {
        showError(locationError);
      } else if (gettingLocation) {
        showError('Please wait while we get your location...');
      } else {
        showError('Location access is required. Please enable location services and refresh the page.');
      }
      return;
    }

    // Basic validation
    if (!mobileNumber || !password) {
      showError('Please fill in all required fields');
      return;
    }

    if (!userExists) {
      showError('User not found. Please check your mobile number.');
      return;
    }

    // If OTP is required but not provided
    if (showOtpInput && !otp) {
      showError('Please enter the OTP sent to your email.');
      return;
    }

    // Dispatch login action
    const loginData = {
      mobileNumber,
      password,
      otp: showOtpInput ? otp : undefined,
      userLocation,
      branch: userRole === 'Staff' ? branch : undefined
    };

    dispatch(login(loginData))
      .unwrap()
      .then((result) => {
        // Handle different response types
        if (result.requiresOTP) {
          // OTP required - show OTP input
          setShowOtpInput(true);
          setOtpSent(true);
          showActionToast('otpSent', true, result.message || 'OTP sent to your email');
        } else if (result.statusCode === 423) {
          // Account locked
          setIsAccountLocked(true);
          const timeMatch = result.message.match(/(\d+) minute\(s\)/);
          if (timeMatch) {
            setLockTimeMinutes(parseInt(timeMatch[1]));
          }
          showError(result.message);
        } else {
          // Successful login handled by useEffect
        }
      })
      .catch((error) => {
        console.error('Login error:', error);
        
        // Handle specific error types
        if (error.message && error.message.includes('Invalid IP Address')) {
          showError('Access denied. Login is only allowed from India.');
        } else if (error.message && error.message.includes('Location access is required')) {
          showError('Location access is required. Please enable location services and try again.');
        } else if (error.message && error.message.includes('locked')) {
          setIsAccountLocked(true);
          const timeMatch = error.message.match(/(\d+) minute\(s\)/);
          if (timeMatch) {
            setLockTimeMinutes(parseInt(timeMatch[1]));
          }
          showError(error.message);
        } else if (error.message && error.message.includes('Invalid or expired OTP')) {
          showError('Invalid or expired OTP. Please request a new OTP.');
          setOtp('');
        } else {
          // Generic login failure
          showActionToast('login', false, 'Invalid mobile number or password');
        }
      });
  };
  
  const handleTimerEnd = () => {
    setIsAccountLocked(false);
    setLockTimeMinutes(0);
    showActionToast('login', true, 'Account unlocked! You can try logging in again.');
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Banner */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-800 relative overflow-hidden">
        {/* Animated Background Gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500 via-purple-500 to-indigo-700 animate-pulse opacity-30"></div>
        
        {/* Floating Particles */}
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-2 h-2 bg-white rounded-full animate-bounce opacity-60" style={{animationDelay: '0s'}}></div>
          <div className="absolute top-20 right-20 w-1 h-1 bg-blue-200 rounded-full animate-ping opacity-40" style={{animationDelay: '1s'}}></div>
          <div className="absolute top-40 left-1/4 w-3 h-3 bg-purple-200 rounded-full animate-pulse opacity-50" style={{animationDelay: '2s'}}></div>
          <div className="absolute bottom-32 right-16 w-2 h-2 bg-indigo-200 rounded-full animate-bounce opacity-30" style={{animationDelay: '3s'}}></div>
          <div className="absolute bottom-20 left-12 w-1 h-1 bg-white rounded-full animate-ping opacity-60" style={{animationDelay: '4s'}}></div>
          <div className="absolute top-1/3 right-1/3 w-2 h-2 bg-blue-300 rounded-full animate-pulse opacity-40" style={{animationDelay: '2.5s'}}></div>
        </div>
        
        {/* Animated Grid Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-full h-full animate-pulse">
            <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
              <defs>
                <pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse">
                  <path d="M 10 0 L 0 0 0 10" fill="none" stroke="white" strokeWidth="0.5"/>
                </pattern>
              </defs>
              <rect width="100" height="100" fill="url(#grid)" />
            </svg>
          </div>
        </div>
        
        {/* Moving Geometric Shapes */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-16 left-8 w-16 h-16 border border-white/20 rotate-45 animate-spin" style={{animationDuration: '20s'}}></div>
          <div className="absolute bottom-24 right-12 w-12 h-12 border border-purple-200/30 rounded-full animate-ping" style={{animationDuration: '3s'}}></div>
          <div className="absolute top-1/2 left-4 w-8 h-8 bg-white/10 rotate-12 animate-pulse" style={{animationDuration: '4s'}}></div>
        </div>
        
        {/* Banner Content */}
        <div className="relative z-10 flex flex-col justify-center items-center text-white p-12 text-center">
          {/* Logo */}
          <div className="mb-8 animate-fade-in">
            <div className="bg-white/20 backdrop-blur-sm rounded-3xl p-6 mb-6 transform hover:scale-105 transition-all duration-300 animate-float">
              <img 
                src="/shahji-logo.png" 
                alt="Shahji Computers" 
                className="h-12 w-auto max-w-35 mx-auto transition-transform duration-300 hover:rotate-3"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
              <div className="hidden">
                <svg className="w-20 h-20 text-white animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
            </div>
            <h1 className="text-4xl font-bold mb-2 animate-slide-up">Shahji Computers</h1>
            <p className="text-xl text-blue-100 animate-slide-up" style={{animationDelay: '0.2s'}}>Enterprise Resource Planning</p>
          </div>
          
          {/* Features */}
          <div className="space-y-6 max-w-md">
            <div className="flex items-center space-x-4 animate-slide-up" style={{animationDelay: '0.4s'}}>
              <div className="bg-white/20 rounded-full p-3 transform hover:scale-110 transition-transform duration-300 hover:bg-white/30">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold">Sales Management</h3>
                <p className="text-blue-100 text-sm">Track and manage all your sales</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4 animate-slide-up" style={{animationDelay: '0.6s'}}>
              <div className="bg-white/20 rounded-full p-3 transform hover:scale-110 transition-transform duration-300 hover:bg-white/30">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold">User Management</h3>
                <p className="text-blue-100 text-sm">Manage staff and permissions</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4 animate-slide-up" style={{animationDelay: '0.8s'}}>
              <div className="bg-white/20 rounded-full p-3 transform hover:scale-110 transition-transform duration-300 hover:bg-white/30">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold">Multi-Branch Support</h3>
                <p className="text-blue-100 text-sm">Katra & Meeratola branches</p>
              </div>
            </div>
          </div>
          
          {/* Bottom Text */}
          <div className="mt-12 text-center">
            <p className="text-blue-100 text-sm">
              Streamline your computer service business with our comprehensive ERP solution
            </p>
          </div>
        </div>
      </div>
      
      {/* Right Login Form */}
      <div className="flex-1 flex items-center justify-center bg-gray-50 px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-md space-y-8">
          {/* Header */}
          <div className="text-center">
            {/* Logo - Always visible */}
            <div className="mb-8">
              <img 
                src="/shahji-logo.png" 
                alt="Shahji Computers" 
                className="h-10 w-auto max-w-25 mx-auto mb-4"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
              <div className="hidden">
                <svg className="w-16 h-16 text-blue-600 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
            </div>
            
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              Welcome Back
            </h2>
            <p className="text-gray-600 mb-8">
              Sign in to your account to continue
            </p>
          </div>

          {/* Login Form */}
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
            <form onSubmit={submitHandler} className="space-y-6">
              {/* Mobile Number Input */}
              <div className="space-y-2">
                <label htmlFor="mobileNumber" className="text-sm font-semibold text-gray-700">
                  Mobile Number
                </label>
                <div className="relative">
                  <input
                    type="text"
                    id="mobileNumber"
                    value={mobileNumber}
                    onChange={(e) => setMobileNumber(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 pr-10"
                    placeholder="Enter your mobile number"
                    required
                  />
                  {checkingUser && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <svg className="animate-spin h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </div>
                  )}
                </div>
                
                {/* User Role Status */}
                {mobileNumber.length >= 10 && !checkingUser && (
                  <div className="mt-2">
                    {userExists ? (
                      <div className={`flex items-center space-x-2 p-3 rounded-lg ${
                        userRole === 'Admin' 
                          ? 'bg-purple-50 border border-purple-200' 
                          : 'bg-blue-50 border border-blue-200'
                      }`}>
                        <div className={`w-2 h-2 rounded-full ${
                          userRole === 'Admin' ? 'bg-purple-500' : 'bg-blue-500'
                        }`}></div>
                        <span className={`text-sm font-medium ${
                          userRole === 'Admin' ? 'text-purple-700' : 'text-blue-700'
                        }`}>
                          {userRole === 'Admin' ? '👑 Admin User' : '👤 Staff User'}
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2 p-3 rounded-lg bg-red-50 border border-red-200">
                        <div className="w-2 h-2 rounded-full bg-red-500"></div>
                        <span className="text-sm font-medium text-red-700">❌ User not found</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Password Input */}
              <div className="space-y-2">
                <label htmlFor="password" className="text-sm font-semibold text-gray-700">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-3 pr-12 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200"
                    placeholder="Enter your password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none transition-colors duration-200"
                  >
                    {showPassword ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {/* Branch Selection - Only for Staff */}
              {showBranchSelection && (
                <div className="space-y-2">
                  <label htmlFor="branch" className="text-sm font-semibold text-gray-700">
                    Select Branch
                  </label>
                  <select
                    id="branch"
                    value={branch}
                    onChange={(e) => setBranch(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200"
                  >
                    <option value="Katra">🏪 Katra Branch</option>
                    <option value="Meeratola">🏪 Meeratola Branch</option>
                  </select>
                </div>
              )}

              {/* OTP Input - Only show when OTP is required */}
              {showOtpInput && (
                <div className="space-y-2">
                  <label htmlFor="otp" className="text-sm font-semibold text-gray-700">
                    Enter OTP
                  </label>
                  <input
                    type="text"
                    id="otp"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="w-full px-4 py-3 border-2 border-blue-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 text-center text-lg font-mono tracking-widest"
                    placeholder="000000"
                    maxLength={6}
                    required
                  />
                  {otpSent && (
                    <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg">
                      <div className="flex items-center">
                        <svg className="w-4 h-4 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                        <span className="text-sm text-blue-700">OTP sent to your email. Check your inbox and enter the 6-digit code.</span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Location Status */}
              <div className="space-y-2">
                <div className={`p-3 rounded-lg border ${
                  userLocation 
                    ? 'bg-green-50 border-green-200' 
                    : gettingLocation 
                    ? 'bg-yellow-50 border-yellow-200'
                    : 'bg-red-50 border-red-200'
                }`}>
                  <div className="flex items-center">
                    {userLocation ? (
                      <>
                        <svg className="w-4 h-4 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span className="text-sm text-green-700 font-medium">📍 Location access granted</span>
                      </>
                    ) : gettingLocation ? (
                      <>
                        <svg className="w-4 h-4 text-yellow-600 mr-2 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        <span className="text-sm text-yellow-700 font-medium">📍 Getting location...</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4 text-red-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636M5.636 18.364l12.728-12.728" />
                        </svg>
                        <span className="text-sm text-red-700 font-medium">📍 Location access required</span>
                      </>
                    )}
                  </div>
                  {locationError && (
                    <p className="text-xs text-red-600 mt-1">{locationError}</p>
                  )}
                </div>
              </div>

              {/* Admin Info */}
              {userRole === 'Admin' && userExists && (
                <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-4 rounded-xl border border-purple-200">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-purple-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-sm text-purple-700 font-medium">Admin has access to all branches</span>
                  </div>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading || !userExists || (!userLocation && !gettingLocation)}
                className={`w-full py-3 text-white rounded-xl font-semibold transition-all duration-200 transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none ${
                  userRole === 'Admin' 
                    ? 'bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700' 
                    : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700'
                }`}
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {showOtpInput ? 'Verifying OTP...' : 'Signing In...'}
                  </div>
                ) : (
                  <div className="flex items-center justify-center">
                    {showOtpInput ? (
                      <>
                        🔐 Verify OTP
                      </>
                    ) : (
                      <>
                        {userRole === 'Admin' ? '👑' : '👤'} Sign In
                      </>
                    )}
                  </div>
                )}
              </button>
            </form>

            {/* Account Lock Timer */}
            {isAccountLocked && lockTimeMinutes > 0 && (
              <div className="mt-6">
                <LockCountdownTimer 
                  lockTimeInMinutes={lockTimeMinutes}
                  onTimerEnd={handleTimerEnd}
                />
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="text-center">
            <p className="text-sm text-gray-500">
              © 2025 Shahji Computers. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginScreen;
